/**
 * CategoriesPage — full CRUD kategorii. Toggle Wydatki/Przychody na górze.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../components/Icons';
import SegmentedControl from '../components/ui/SegmentedControl';
import { useCategoriesStore, type Category } from '../features/categories/categoriesStore';
import { CategoryEditModal } from '../components/categories/CategoryEditModal';
import { getCategoryIcon } from '../lib/categoryIcons';
import { safeColor } from '../lib/utils';

export function CategoriesPage() {
  const categories = useCategoriesStore((s) => s.categories);
  const [type, setType] = useState<'expense' | 'income'>('expense');
  const [editing, setEditing] = useState<Category | 'new' | null>(null);

  const filtered = useMemo(
    () => categories.filter((c) => c.type === type).sort((a, b) => a.order - b.order),
    [categories, type],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/settings" className="w-9 h-9 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition" aria-label="Wstecz">
            <Icons.ChevronLeft size={20} />
          </Link>
          <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white">Kategorie</h1>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-3 py-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold flex items-center gap-1.5 shadow-card transition"
        >
          <Icons.Plus size={16} strokeWidth={2.5} />
          Nowa
        </button>
      </div>

      <SegmentedControl<'expense' | 'income'>
        options={[
          { key: 'expense', label: 'Wydatki' },
          { key: 'income', label: 'Przychody' },
        ]}
        value={type}
        onChange={setType}
        fullWidth
      />

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <Icons.ChartPie size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-slate-500 dark:text-slate-400">Brak kategorii w tej grupie</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card overflow-hidden">
          {filtered.map((c, i) => {
            const Icon = getCategoryIcon(c.icon);
            const color = safeColor(c.color, '#94a3b8');
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setEditing(c)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition text-left ${
                  i > 0 ? 'border-t border-slate-100 dark:border-slate-700' : ''
                }`}
              >
                <span
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${color}22`, color }}
                >
                  <Icon size={18} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{c.name}</p>
                  {c.isDefault && (
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Domyślna</p>
                  )}
                </div>
                <Icons.ChevronRight size={16} className="text-slate-300 dark:text-slate-600" />
              </button>
            );
          })}
        </div>
      )}

      {editing && (
        <CategoryEditModal
          category={editing === 'new' ? null : editing}
          defaultType={type}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
